import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Box,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DataObjectIcon from "@mui/icons-material/DataObject";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LoadingButton from "./LoadingButton";
import AITextAssistantDialog from "./AITextAssistantDialog";

export interface VariableField {
  key: string;
  label: string;
  icon?: React.ReactNode;
  isCustom?: boolean;
}

interface PopOutTextDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (value: string) => void;
  value: string;
  title?: string;
  description?: string;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  variableFields?: VariableField[];
  customVariableFields?: VariableField[];
  // AI Assistant props
  aiContext?: string;
  getToken?: () => Promise<string>;
  companyId?: string;
  // Slash command trigger for variables
  enableSlashTrigger?: boolean;
}

const PopOutTextDialog: React.FC<PopOutTextDialogProps> = ({
  open,
  onClose,
  onSave,
  value,
  title = "Edit Text",
  description,
  placeholder,
  rows = 20,
  maxLength,
  variableFields = [],
  customVariableFields = [],
  aiContext,
  getToken,
  companyId,
  enableSlashTrigger = false,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [hasChanges, setHasChanges] = useState(false);
  const [variableMenuAnchorEl, setVariableMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const textFieldRef = useRef<HTMLTextAreaElement>(null);
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  const [slashPosition, setSlashPosition] = useState<number | null>(null);
  const savedScrollTop = useRef<number>(0);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);

  const hasVariables = variableFields.length > 0 || customVariableFields.length > 0;
  const hasAiAssistant = !!getToken && !!companyId;

  useEffect(() => {
    if (open) {
      setLocalValue(value);
      setHasChanges(false);
      setCursorPosition(null);
      setSlashPosition(null);
    }
  }, [open, value]);

  // Restore cursor position after inserting variable
  useEffect(() => {
    if (cursorPosition !== null && textFieldRef.current) {
      const input = textFieldRef.current;
      input.focus();
      input.setSelectionRange(cursorPosition, cursorPosition);
      input.scrollTop = savedScrollTop.current;
      setCursorPosition(null);
    }
  }, [cursorPosition]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    if (!maxLength || newValue.length <= maxLength) {
      setLocalValue(newValue);
      setHasChanges(newValue !== value);
    }

    // Check for "/" trigger when enableSlashTrigger is true
    if (
      enableSlashTrigger &&
      hasVariables &&
      textFieldRef.current &&
      newValue[textFieldRef.current.selectionStart - 1] === "/"
    ) {
      setSlashPosition(textFieldRef.current.selectionStart - 1);
      savedScrollTop.current = textFieldRef.current.scrollTop;
      setVariableMenuAnchorEl(textFieldRef.current);
    }
  };

  const handleInsertVariable = (key: string) => {
    const variableText = `{{${key}}}`;

    if (slashPosition !== null) {
      // Insert at slash position, replacing the "/"
      const before = localValue.substring(0, slashPosition);
      const after = localValue.substring(slashPosition + 1);
      const newValue = before + variableText + after;

      if (!maxLength || newValue.length <= maxLength) {
        setLocalValue(newValue);
        setHasChanges(newValue !== value);
        setCursorPosition(slashPosition + variableText.length);
      }
      setSlashPosition(null);
    } else {
      const textarea = textFieldRef.current;
      if (textarea) {
        const start = textarea.selectionStart || 0;
        const end = textarea.selectionEnd || 0;
        const newValue = localValue.slice(0, start) + variableText + localValue.slice(end);

        if (!maxLength || newValue.length <= maxLength) {
          setLocalValue(newValue);
          setHasChanges(newValue !== value);
          setCursorPosition(start + variableText.length);
        }
      } else {
        const newValue = localValue + variableText;
        if (!maxLength || newValue.length <= maxLength) {
          setLocalValue(newValue);
          setHasChanges(newValue !== value);
        }
      }
    }

    setVariableMenuAnchorEl(null);
  };

  const handleMenuClose = () => {
    setVariableMenuAnchorEl(null);
    setSlashPosition(null);
  };

  const handleSave = () => {
    onSave(localValue);
    setHasChanges(false);
  };

  const handleAiAccept = (generatedText: string) => {
    setLocalValue(generatedText);
    setHasChanges(generatedText !== value);
  };

  const handleClose = () => {
    if (hasChanges) {
      setCloseConfirmOpen(true);
      return;
    }
    onClose();
  };

  const handleConfirmClose = () => {
    setCloseConfirmOpen(false);
    onClose();
  };

  const characterCount = localValue.length;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          height: "80vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">{title}</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {hasAiAssistant && (
              <Tooltip title="AI Writing Assistant">
                <IconButton
                  onClick={() => setAiDialogOpen(true)}
                  size="small"
                >
                  <AutoAwesomeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
          pt: 1,
        }}
      >
        {description && (
          <DialogContentText sx={{ mb: 2 }}>
            {description}
          </DialogContentText>
        )}
        <TextField
          inputRef={textFieldRef}
          value={localValue}
          onChange={handleChange}
          multiline
          rows={rows}
          fullWidth
          placeholder={placeholder}
          variant="outlined"
          error={maxLength ? characterCount > maxLength : false}
          sx={{
            flex: 1,
            "& .MuiInputBase-root": {
              height: "100%",
              alignItems: "flex-start",
            },
            "& .MuiInputBase-input": {
              height: "100% !important",
              overflowY: "auto !important",
              fontFamily: "monospace",
              fontSize: "14px",
            },
          }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {hasVariables && (
              <>
                <Tooltip title="Insert personalization variable">
                  <Button
                    size="small"
                    startIcon={<DataObjectIcon />}
                    onClick={(e) => setVariableMenuAnchorEl(e.currentTarget)}
                    sx={{ textTransform: "none" }}
                  >
                    Insert Variable
                  </Button>
                </Tooltip>
                <Menu
                  anchorEl={variableMenuAnchorEl}
                  open={Boolean(variableMenuAnchorEl)}
                  onClose={handleMenuClose}
                  PaperProps={{ sx: { maxHeight: 400, width: 280 } }}
                >
                  {variableFields.length > 0 && (
                    <>
                      <Typography
                        variant="caption"
                        sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}
                      >
                        Member Fields
                      </Typography>
                      {variableFields.map((field) => (
                        <MenuItem
                          key={field.key}
                          onClick={() => handleInsertVariable(field.key)}
                          dense
                        >
                          <ListItemIcon>
                            {field.icon || <DataObjectIcon fontSize="small" />}
                          </ListItemIcon>
                          <ListItemText
                            primary={field.label}
                            secondary={`{{${field.key}}}`}
                            secondaryTypographyProps={{
                              variant: "caption",
                              fontFamily: "monospace",
                            }}
                          />
                        </MenuItem>
                      ))}
                    </>
                  )}
                  {customVariableFields.length > 0 && (
                    <>
                      {variableFields.length > 0 && <Divider sx={{ my: 1 }} />}
                      <Typography
                        variant="caption"
                        sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}
                      >
                        Custom Fields
                      </Typography>
                      {customVariableFields.map((field) => (
                        <MenuItem
                          key={field.key}
                          onClick={() => handleInsertVariable(field.key)}
                          dense
                        >
                          <ListItemIcon>
                            {field.icon || <DataObjectIcon fontSize="small" />}
                          </ListItemIcon>
                          <ListItemText
                            primary={field.label}
                            secondary={`{{${field.key}}}`}
                            secondaryTypographyProps={{
                              variant: "caption",
                              fontFamily: "monospace",
                            }}
                          />
                        </MenuItem>
                      ))}
                    </>
                  )}
                </Menu>
              </>
            )}
            {enableSlashTrigger && hasVariables && (
              <Typography variant="caption" color="text.secondary">
                Type{" "}
                <span
                  style={{
                    backgroundColor: "rgba(128, 128, 128, 0.2)",
                    padding: "2px 4px",
                    borderRadius: "4px",
                  }}
                >
                  /
                </span>{" "}
                to insert variables
              </Typography>
            )}
            <Typography
              variant="caption"
              color={maxLength && characterCount > maxLength ? "error" : "text.secondary"}
            >
              {characterCount} characters
              {maxLength && ` / ${maxLength}`}
            </Typography>
          </Box>
          {hasChanges && (
            <Typography variant="caption" color="warning.main">
              Unsaved changes
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={false}
          disabled={!hasChanges}
        >
          Save
        </LoadingButton>
      </DialogActions>

      {/* AI Text Assistant Dialog */}
      {hasAiAssistant && getToken && companyId && (
        <AITextAssistantDialog
          open={aiDialogOpen}
          onClose={() => setAiDialogOpen(false)}
          onAccept={handleAiAccept}
          existingText={localValue}
          fieldContext={aiContext || title || ""}
          getToken={getToken}
          companyId={companyId}
        />
      )}

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog
        open={closeConfirmOpen}
        onClose={() => setCloseConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Are you sure you want to close without saving?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmClose}
            color="error"
            variant="contained"
          >
            Close Without Saving
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default PopOutTextDialog;